import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, Music, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';

const BeatCard = ({ beat, index = 0 }) => {
  const { currentBeat, isPlaying, setIsPlaying, playBeat } = useApp();
  const isCurrentPlaying = currentBeat?.beat_id === beat.beat_id && isPlaying;
  const navigate = useNavigate();

  const handlePlay = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    
    // Use preview_url if available, otherwise fall back to audio_url
    const audioUrl = beat.preview_url || beat.audio_url;
    
    if (!audioUrl) {
      console.error('No audio URL available for this beat');
      return;
    }
    
    if (currentBeat?.beat_id === beat.beat_id) {
      // Same beat - just toggle play/pause
      setIsPlaying(!isPlaying);
    } else {
      // Different beat - use optimized playBeat
      playBeat(beat);
    }
  };

  const handleCardClick = () => {
    navigate(`/beats/${beat.beat_id}`);
  };

  return (
    <div 
      className="glass-card p-4 group animate-fade-in-up cursor-pointer" 
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`beat-card-${beat.beat_id}`}
      onClick={handleCardClick}
    >
      <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-neutral-900 border border-[#D4AF37]/10">
        {beat.image_url ? (
          <img 
            src={beat.image_url} 
            alt={beat.title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music size={48} className="text-[#D4AF37]/30" />
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Play button - always visible */}
        <button
          onClick={handlePlay}
          className="absolute inset-0 flex items-center justify-center transition-all duration-300"
          data-testid={`play-btn-${beat.beat_id}`}
        >
          <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center transform transition-all duration-300 hover:scale-110 shadow-lg ${isCurrentPlaying ? 'animate-pulse-glow scale-100' : 'scale-90 opacity-90 group-hover:opacity-100 group-hover:scale-100'}`}>
            {isCurrentPlaying ? (
              <Pause size={28} fill="black" stroke="black" />
            ) : (
              <Play size={28} fill="black" stroke="black" className="ml-1" />
            )}
          </div>
        </button>
        
        {/* Playing indicator */}
        {isCurrentPlaying && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/70 px-2 py-1 rounded-full">
            <div className="flex gap-0.5">
              <span className="w-1 h-3 bg-[#D4AF37] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
              <span className="w-1 h-4 bg-[#D4AF37] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
              <span className="w-1 h-2 bg-[#D4AF37] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs text-[#D4AF37] font-medium">Playing</span>
          </div>
        )}
      </div>

      <h3 className="font-bold text-lg mb-1 text-white group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Syne' }}>
        {beat.title}
      </h3>
      <div className="flex items-center gap-2 text-sm text-neutral-500 mb-3 mono">
        <span>{beat.bpm} BPM</span>
        <span>•</span>
        <span>{beat.key}</span>
        <span>•</span>
        <span>{beat.genre}</span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[#D4AF37] font-bold text-lg">${beat.lease_price} CAD</span>
        <span 
          className="text-sm text-neutral-400 hover:text-[#D4AF37] flex items-center gap-1 group/link transition-all"
        >
          View Licenses 
          <ChevronRight size={16} className="transform group-hover/link:translate-x-1 transition-transform" />
        </span>
      </div>
    </div>
  );
};

export default BeatCard;
